import Page from 'attendee/theme/Page'
import React, {useEffect, useMemo, useState} from 'react'
import {useEvent} from 'event/providers/EventProvider'
import ModalVideo from 'react-modal-video'
import {useAttendeeBilling} from 'attendee/Billing/Providers/AttendeeBillingProvider'
import {useAuth} from 'attendee/Auth/Providers/AuthProvider'
import {routes} from 'routes/routes'
import {LocalLink} from 'i18n/LocalLink'
import {useLocalizedNavigate} from 'i18n/useLocalizedNavigate'
import useTicketPayment from 'attendee/Billing/Payments/useTicketPayment'
import {
  formatEventTicketPriceLabel,
  getTicketPrice,
  getTicketPriceMax,
  parseTicketFeaturesString,
} from 'event/ticketPrices'
import type {EventTicketOption} from 'event/models/Event'
import {publicImageUrl} from 'organization/ImageSelector'
import {getEventImageUrl} from 'event/models/Event'
import defaultEventImage from 'images/defaultEventImage.webp'
import {Comments} from 'event/Comments'
import CommentsProvider from 'event/Comments/CommentsProvider'
import {useQueryParams} from 'utils/lib/url'
import {eventRoutes} from 'event/EventRoutes'
import styled from 'styled-components'
import {Helmet} from 'react-helmet'
import {
  DEFAULT_OG_IMAGE,
  SITE_ORIGIN,
  stripHtmlToText,
  truncateMetaDescription,
} from 'seo/siteMeta'
import {confirmAlert} from 'react-confirm-alert'
import {formatUserDay} from 'utils/lib/date'
import moment from 'moment'
import {useI18n} from 'i18n/I18nContext'
import {withLocalePath} from 'i18n/localePaths'
import {LOCALE_LABELS, isAppLocale, type AppLocale} from 'i18n/locales'
import {toast} from 'react-toastify'

function TicketTierList(props: {
  options: EventTicketOption[]
  selectedId: number | null
  onSelect: (id: number) => void
  chooseLabel: string
  freeLabel: string
}) {
  const {options, selectedId, onSelect, chooseLabel, freeLabel} = props
  return (
    <div
      className="mb-20"
      role="radiogroup"
      aria-label={chooseLabel}
      style={{marginTop: 12}}
    >
      <p className="mb-10" style={{fontWeight: 600}}>
        {chooseLabel}
      </p>
      {options.map((tier) => {
        const checked = selectedId === tier.id
        const priceLabel =
          tier.price <= 0 ? freeLabel : `${tier.price.toFixed(2)} €`
        return (
          <label
            key={tier.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              cursor: 'pointer',
              border: checked
                ? '2px solid var(--clr-theme-3, #1ea9f8)'
                : '1px solid var(--clr-border-1, #ddd)',
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 10,
              background: checked ? 'rgba(30, 169, 248, 0.06)' : 'transparent',
            }}
          >
            <span style={{display: 'flex', alignItems: 'center', gap: 10}}>
              <input
                type="radio"
                name="eventTicketTier"
                checked={checked}
                onChange={() => onSelect(tier.id)}
              />
              <span style={{fontWeight: 600}}>{tier.name}</span>
            </span>
            <span style={{whiteSpace: 'nowrap'}}>{priceLabel}</span>
          </label>
        )
      })}
    </div>
  )
}

function eventLanguageLabel(event: {locale?: string}, uiLocale: AppLocale) {
  const raw = event.locale?.toLowerCase()
  if (raw && isAppLocale(raw)) return LOCALE_LABELS[raw]
  return LOCALE_LABELS[uiLocale]
}

export function View() {
  const {t, locale} = useI18n()
  const {event, loading} = useEvent()
  const {user} = useAuth()
  const navigate = useLocalizedNavigate()
  const {payPayment} = useAttendeeBilling()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const openVideoModal = () => setIsOpen(!isOpen)
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null)
  const {preview} = useQueryParams()
  const eventRoute = event ? eventRoutes(event) : (undefined as any)

  const ticketOptions: EventTicketOption[] = useMemo(() => {
    if (!event?.tickets?.length) return []
    return [...event.tickets].sort(
      (a, b) => a.sort_order - b.sort_order || a.id - b.id,
    )
  }, [event?.tickets])

  useEffect(() => {
    if (!ticketOptions.length) {
      setSelectedTicketId(null)
      return
    }
    setSelectedTicketId((prev) => {
      if (prev != null && ticketOptions.some((t) => t.id === prev)) {
        return prev
      }
      return ticketOptions[0].id
    })
  }, [ticketOptions])

  const selectedTicket =
    ticketOptions.find((t) => t.id === selectedTicketId) ?? ticketOptions[0]

  const {payment} = useTicketPayment(event, selectedTicketId)

  const handleBuyTicket = () => {
    if (!event) return

    if (!user) {
      navigate(routes.login)
      return
    }

    if (!selectedTicketId) {
      toast.warning(t('eventView.selectTicketPrompt'))
      return
    }

    const isFreeEvent =
      selectedTicket != null ? selectedTicket.price <= 0 : event.is_free

    if (isFreeEvent) {
      // Directly call payment with blank paymentMethodId, because it's free event
      confirmAlert({
        message: t('eventView.confirmFreeTicket').replace('{name}', event.name),
        closeOnClickOutside: false,
        buttons: [
          {
            label: t('eventView.confirmYes'),
            onClick: async () => {
              payment()('')
            },
          },
          {
            label: t('eventView.confirmNo'),
            onClick: () => {
              // nothing
            },
          },
        ],
      })

      return
    }

    payPayment(payment())
  }

  useEffect(() => {
    if (!event && !loading) {
      navigate(routes.login)
    }
  }, [event, loading, navigate])

  useEffect(() => {
    if (!event) return
    const isPreview = preview === '1'

    if (!isPreview && !event.is_public && !event.is_ended && event.id < 30000) {
      navigate(routes.root)
    }
  }, [event, preview, navigate])

  if (!event) return null

  const image = getEventImageUrl(event, 1)
  const eventImage = image ? publicImageUrl(image) : defaultEventImage

  const eventVideoimage = getEventImageUrl(event, 2)
  const videoImage = eventVideoimage
    ? publicImageUrl(eventVideoimage)
    : defaultEventImage

  const eventPrice = formatEventTicketPriceLabel(event, t)

  const featureList =
    selectedTicket != null
      ? parseTicketFeaturesString(selectedTicket.ticket_features)
      : parseTicketFeaturesString(event.ticket_features)

  const displayTicketName =
    selectedTicket?.name?.trim() || event.ticket_name || '\u00a0'

  const headlinePrice =
    selectedTicket != null
      ? selectedTicket.price <= 0
        ? t('eventRow.free')
        : `${selectedTicket.price.toFixed(2)} €`
      : eventPrice

  const canEnd =
    moment(`${event.date} ${event.time}`).add(30, 'minutes') < moment()

  const eventDate =
    event.date instanceof Date
      ? event.date
      : new Date(event.date as unknown as string)
  const whenLabel = formatUserDay(eventDate)
  const snippetSource = event.short_description || event.description || ''
  const metaDescription = truncateMetaDescription(
    [
      t('eventView.metaEventLine').replace('{name}', event.name),
      whenLabel || '',
      t('eventView.metaTicket').replace('{price}', eventPrice),
      stripHtmlToText(snippetSource),
    ]
      .filter(Boolean)
      .join(' '),
  )
  const pageUrl = `${SITE_ORIGIN}${withLocalePath(locale, `/${event.slug}`)}`
  const ogImage =
    image && publicImageUrl(image) ? publicImageUrl(image)! : DEFAULT_OG_IMAGE

  return (
    <Page>
      <>
        <Helmet>
          <title>{`${event.name} | ${t('eventView.helmetTitleSuffix')}`}</title>
          <meta name="description" content={metaDescription} />
          <link rel="canonical" href={pageUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={`${event.name} | Eventa.tv`} />
          <meta property="og:description" content={metaDescription} />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:image" content={ogImage} />
          <meta property="og:image:secure_url" content={ogImage} />
          <meta property="og:image:type" content="image/jpeg" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${event.name} | Eventa.tv`} />
          <meta name="twitter:description" content={metaDescription} />
          <meta name="twitter:image" content={ogImage} />
        </Helmet>
        <div className="event__details-area pt-55 pb-120">
          <div className="container">
            <div className="row">
              <div>
                <NameHolder className="body__card-wrapper mb-20">
                  <div className="card__title-inner">
                    <NameH4 className="event__information-title">
                      {event?.name}
                    </NameH4>
                  </div>
                </NameHolder>
              </div>
            </div>

            <div className="row reverse-on-mobile">
              <div className="col-xxl-7 col-xl-7">
                <div className="event__details-left">
                  <div className="body__card-wrapper mb-20">
                    <div className="review__main-wrapper">
                      <div className="review__tab">
                        <nav>
                          <div
                            className="nav nav-tabs"
                            id="nav-tab"
                            role="tablist"
                          >
                            <button
                              className="nav-link active"
                              id="nav-home-tab"
                              data-bs-toggle="tab"
                              data-bs-target="#nav-home"
                              type="button"
                              role="tab"
                              aria-controls="nav-home"
                              aria-selected="true"
                            >
                              {t('eventView.tabInfo')}
                            </button>
                            <button
                              className="nav-link"
                              id="nav-profile-tab"
                              data-bs-toggle="tab"
                              data-bs-target="#nav-profile"
                              type="button"
                              role="tab"
                              aria-controls="nav-profile"
                              aria-selected="false"
                            >
                              {t('eventView.tabComments')}
                            </button>
                          </div>
                        </nav>
                        <div className="tab-content" id="nav-tabContent">
                          <div
                            className="tab-pane fade show active"
                            id="nav-home"
                            role="tabpanel"
                            aria-labelledby="nav-home-tab"
                            tabIndex={0}
                          >
                            <div className="about__event-thumb w-img mt-40">
                              <img
                                style={{width: '100%', height: 'auto'}}
                                src={eventImage}
                                alt="info"
                              />
                            </div>
                            <div className="ql-snow mt-30">
                              <div
                                className="ql-editor"
                                dangerouslySetInnerHTML={{
                                  __html: event.description,
                                }}
                              />
                            </div>
                          </div>
                          <div
                            className="tab-pane fade"
                            id="nav-profile"
                            role="tabpanel"
                            aria-labelledby="nav-profile-tab"
                            tabIndex={0}
                          >
                            <CommentsProvider>
                              <Comments />
                            </CommentsProvider>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* right side content */}

              <div className="col-xxl-5 col-xl-5">
                <div className="event__details-right">
                  <div className="body__card-wrapper mb-20">
                    {event.has_ticket && (
                      <div>
                        <LocalLink
                          className={'input__btn w-100 mb-15'}
                          target="_blank"
                          to={eventRoute.preview}
                        >
                          {t('eventView.enterStream')}
                        </LocalLink>
                        <p className="w-100 mb-0" style={{textAlign: 'center'}}>
                          {t('eventView.hasTicket')}
                        </p>
                      </div>
                    )}
                    {!event.has_ticket && (
                      <div className="pricing__item">
                        <div className="pricing__item-header">
                          <div>
                            <span className="pricing__item-title">
                              {headlinePrice}
                            </span>
                            {ticketOptions.length > 1 &&
                            getTicketPrice(event) !==
                              getTicketPriceMax(event) ? (
                              <span
                                className="d-block mt-5"
                                style={{fontSize: '0.85rem', opacity: 0.85}}
                              >
                                {t('eventView.ticketOptionsFrom').replace(
                                  '{range}',
                                  eventPrice,
                                )}
                              </span>
                            ) : null}
                          </div>
                          <div className="pricing__item-lable">
                            <span className="pricing__item-label">
                              {displayTicketName}
                            </span>
                            <svg
                              className="lable-border"
                              width="97"
                              height="54"
                              viewBox="0 0 97 54"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1.55449 2.53125L0.518162 0.84375L0.944232 0.582093L0.893817 0.5H1.01039V0H3.03123V0.5H5.05206V0H7.0729V0.5H9.09373V0H11.1146V0.5H13.1354V0H15.1562V0.5H17.1771V0H19.1979V0.5H21.2188V0H23.2396V0.5H25.2604V0H27.2813V0.5H29.3021V0H31.3229V0.5H33.3438V0H35.3646V0.5H37.3854V0H39.4063V0.5H41.4271V0H43.4479V0.5H45.4688V0H47.4896V0.5H49.5104V0H51.5313V0.5H53.5521V0H55.5729V0.5H57.5938V0H59.6146V0.5H61.6354V0H63.6562V0.5H65.6771V0H67.6979V0.5H69.7188V0H71.7396V0.5H73.7604V0H75.7812V0.5H77.8021V0H79.8229V0.5H81.8438V0H83.8646V0.5H85.8854V0H87.9062V0.5H89.9271V0H91.9479V0.5H93.9688V0H95.9896V0.5H96.5V0.964301H97V2.89287H96.5V4.82144H97V6.75001H96.5V8.67858H97V10.6072H96.5V12.5357H97V14.4643H96.5V16.3929H97V18.3214H96.5V20.25H97V22.1786H96.5V24.1071H97V26.0357H96.5V27.9643H97V29.8929H96.5V31.8214H97V33.75H96.5V35.6786H97V37.6071H96.5V39.5357H97V41.4643H96.5V43.3929H97V45.3214H96.5V47.25H97V49.1786H96.5V51.1071H97V53.0357H96.5V53.5H95.9896V54H93.9688V53.5H91.9479V54H89.9271V53.5H87.9063V54H85.8854V53.5H83.8646V54H81.8438V53.5H79.8229V54H77.8021V53.5H75.7812V54H73.7604V53.5H71.7396V54H69.7187V53.5H67.6979V54H65.6771V53.5H63.6562V54H61.6354V53.5H59.6146V54H57.5937V53.5H55.5729V54H53.5521V53.5H51.5312V54H49.5104V53.5H47.4896V54H45.4687V53.5H43.4479V54H41.4271V53.5H39.4062V54H37.3854V53.5H35.3646V54H33.3438V53.5H31.3229V54H29.3021V53.5H27.2813V54H25.2604V53.5H23.2396V54H21.2188V53.5H19.1979V54H17.1771V53.5H15.1562V54H13.1354V53.5H11.1146V54H9.09375V53.5H7.07292V54H5.05208V53.5H3.03125V54H1.01042V53.5H0.893817L0.944236 53.4179L0.518167 53.1562L1.55449 51.4688L1.98056 51.7304L3.01688 50.0429L2.59082 49.7812L3.62714 48.0938L4.05321 48.3554L5.08953 46.6679L4.66346 46.4062L5.69979 44.7188L6.12586 44.9804L7.16218 43.2929L6.73611 43.0312L7.77244 41.3438L8.19851 41.6054L9.23483 39.9179L8.80876 39.6562L9.84509 37.9688L10.2712 38.2304L11.3075 36.5429L10.8814 36.2812L11.9177 34.5938L12.3438 34.8554L13.3801 33.1679L12.9541 32.9062L13.9904 31.2188L14.4165 31.4804L15.4528 29.7929L15.0267 29.5313L16.063 27.8438L16.4891 28.1054L17.0073 27.2617L17.168 27L17.0073 26.7383L16.4891 25.8946L16.063 26.1562L15.0267 24.4687L15.4528 24.2071L14.4165 22.5196L13.9904 22.7812L12.9541 21.0937L13.3801 20.8321L12.3438 19.1446L11.9177 19.4062L10.8814 17.7188L11.3075 17.4571L10.2712 15.7696L9.84508 16.0312L8.80876 14.3438L9.23483 14.0821L8.19851 12.3946L7.77244 12.6562L6.73611 10.9688L7.16218 10.7071L6.12586 9.01959L5.69979 9.28125L4.66346 7.59375L5.08953 7.33209L4.05321 5.64459L3.62714 5.90625L2.59081 4.21875L3.01688 3.95709L1.98056 2.26959L1.55449 2.53125Z"
                                stroke="#1EA9F8"
                                stroke-dasharray="2 2"
                              ></path>
                            </svg>
                          </div>
                        </div>
                        {ticketOptions.length > 1 ? (
                          <TicketTierList
                            options={ticketOptions}
                            selectedId={selectedTicketId}
                            onSelect={setSelectedTicketId}
                            chooseLabel={t('eventView.chooseTicket')}
                            freeLabel={t('eventRow.free')}
                          />
                        ) : null}
                        {featureList.length > 0 && (
                          <div className="pricing__item-content">
                            <ul>
                              {featureList.map((feature, idx) => (
                                <li key={idx}>
                                  <i className="flaticon-comment"></i> {feature}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {!event.is_ended && (
                          <div className="pricing__btn-wrap">
                            <button
                              className={'pricing__btn'}
                              onClick={() => {
                                handleBuyTicket()
                              }}
                            >
                              {selectedTicket != null &&
                              selectedTicket.price <= 0
                                ? t('eventView.getTicket')
                                : t('eventView.buyTicket')}
                            </button>
                          </div>
                        )}
                        {canEnd && !event.is_ended && (
                          <EndedLabel>
                            <b>{t('eventView.warningTitle')}</b>{' '}
                            {t('eventView.warningBody')}
                          </EndedLabel>
                        )}
                        {event.is_ended && (
                          <EndedLabel>{t('eventView.eventEnded')}</EndedLabel>
                        )}
                      </div>
                    )}

                    {event.video && (
                      <div className="review__demo-wrapper mt-30">
                        <div className="review__demo-thumb w-img mb-25">
                          <img
                            style={{width: '100%', height: 'auto'}}
                            src={videoImage}
                            alt="video"
                          />
                          <span className="review__play-btn">
                            <button
                              type="button"
                              className="video__play-btn popup-video"
                              onClick={() => {
                                openVideoModal()
                              }}
                            >
                              <i className="fa-solid fa-play"></i>
                              {t('eventView.playVideo')}
                            </button>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="body__card-wrapper mb-20">
                    <div className="event__meta-time">
                      <ul>
                        <li>
                          <span> {t('eventView.labelTicketPrice')} </span>
                          {eventPrice}
                        </li>
                        <li>
                          <span> {t('eventView.labelDate')} </span>
                          {event.date
                            ? formatUserDay(event.date)
                            : t('eventRow.noInfo')}
                        </li>
                        <li>
                          <span>{t('eventView.labelTime')}</span>
                          {event.time ? event.time : t('eventRow.noInfo')}
                        </li>
                        <li>
                          <span>{t('eventView.labelSeats')} </span>
                          {event.available_seats}
                        </li>
                        <li>
                          <span>{t('eventView.labelLanguage')} </span>
                          {eventLanguageLabel(event, locale)}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <ModalVideo
                channel="youtube"
                isOpen={isOpen}
                videoId={getYoutubeVideoId(event.video)}
                onClose={() => {
                  openVideoModal()
                }}
              />
            </div>
          </div>
        </div>
      </>
    </Page>
  )
}

function getYoutubeVideoId(url: string) {
  if (!url) {
    return ''
  }

  var regExp =
    // eslint-disable-next-line
    /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/
  var match = url.match(regExp)
  return match && match[2].length === 11 ? match[2] : ''
}

const NameHolder = styled.div`
  background: var(--clr-gradient-14);
  padding: 15px;
`

const NameH4 = styled.h4`
  font-weight: bold;
  color: white;
`

const EndedLabel = styled.div`
  margin-top: 15px;
  padding: 10px;
  text-align: center;
  border: 1px solid #f5c6cb;
  border-radius: 5px;
`
